import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { V2 } from '../core/math';
import type { System } from '../core/system';
import { onQualityTier, qualityTier } from '../render/qualityTier';
import type { ParticleService } from '../vehicles/fx/particles';
import type { Vehicle } from '../vehicles/types';
import type { VehicleService } from '../vehicles/vehicleSystem';
import { WEAPONS, type WeaponId } from '../weapons/weaponData';

/**
 * Melee feel (docs/WEAPONS.md §3 "What a blow looks like"): what a landed blow shows besides the flinch.
 * A white pop and a few motes at the contact point, a dust ring where a knockdown lands, a camera kick in
 * the direction of the blow with a beat of hit-stop by the weapon's weight, the knife's slash streak at the
 * moment of contact, and a bat or a crowbar on a car: sparks and a scuff on the panel. Listens to
 * `meleeContact` (the frame the swing connects, weaponSystem `contact()`) and `weaponHit`; writes nothing
 * back to the simulation. Bullets landing are fx/impactFx.ts's; the hit-flash on the struck figure is the
 * character views' (`hitFlash` in render/characters/characterLook.ts, driven by the same hit notes as the flinch).
 *
 * Cost: everything goes through the pooled particle service (no draw call of its own on any tier); the
 * scuffs are one InstancedMesh (one draw call while any car carries one), left off on LOW where the
 * bodywork crumple (the vehicles' dents) shows the blow on its own.
 */

/** Hit-stop (s) by the blow: a fist or a blade is a snap, a bat or a crowbar lands heavier, a knockdown heaviest. */
export const HIT_STOP = { light: 0.04, heavy: 0.07, knockdown: 0.09 } as const;

/** Heavy: the blunt two-handers (the ones that dent a car); everything else, a pistol-whip included, is light. */
export function isHeavyBlow(weapon: WeaponId): boolean {
  return WEAPONS[weapon].melee.vehicleDamage > 0;
}

export function meleeHitStop(weapon: WeaponId, knocked: boolean, killed: boolean): number {
  if (knocked || killed) return HIT_STOP.knockdown;
  return isHeavyBlow(weapon) ? HIT_STOP.heavy : HIT_STOP.light;
}

/** Camera kick (m) along the blow, and trauma shake, by the same tiers. */
export function meleeKick(weapon: WeaponId, knocked: boolean, killed: boolean): { nudge: number; shake: number } {
  if (knocked || killed) return { nudge: 0.26, shake: 0.2 };
  return isHeavyBlow(weapon) ? { nudge: 0.18, shake: 0.14 } : { nudge: 0.1, shake: 0.08 };
}

/**
 * `weaponHit` does not say whether it was a blow or a bullet, and a gun in hand can do either (a
 * pistol-whip). A blow it is when the weapon cannot fire, or when the last contact came after the last shot.
 */
export function wasBlow(firearm: boolean, lastContactAt: number, lastFiredAt: number): boolean {
  return !firearm || lastContactAt > lastFiredAt;
}

/**
 * Where a scuff goes on a car hit at `local` (car space, +X left, +Z front) when the bodywork itself gave
 * no answer (see `scuffOnMesh`): the nearer face of the body box, at the panel's shoulder, facing out
 * (yaw 0 = front, ±π/2 = the sides). `out` is reused.
 */
export function scuffOnBody(local: V2, halfW: number, halfL: number, height: number, out = { x: 0, y: 0, z: 0, yaw: 0 }): { x: number; y: number; z: number; yaw: number } {
  const ax = Math.abs(local.x) / Math.max(0.1, halfW);
  const az = Math.abs(local.z) / Math.max(0.1, halfL);
  const sx = local.x < 0 ? -1 : 1;
  const sz = local.z < 0 ? -1 : 1;
  if (ax > az) {
    out.x = sx * (halfW + SCUFF_LIFT);
    out.z = Math.max(-halfL * 0.9, Math.min(halfL * 0.9, local.z));
    out.yaw = (sx * Math.PI) / 2;
  } else {
    out.z = sz * (halfL + SCUFF_LIFT);
    out.x = Math.max(-halfW * 0.8, Math.min(halfW * 0.8, local.x));
    out.yaw = sz > 0 ? 0 : Math.PI;
  }
  out.y = Math.max(0.4, Math.min(1.1, height * 0.62));
  return out;
}
/** The decal sits a touch off the panel so it never fights the paint for depth. */
const SCUFF_LIFT = 0.015;

/** Contact height (m) by the blow: a fist at the jaw, a swung weapon at the chest, a blade low and quick. */
export function contactHeight(weapon: WeaponId): number {
  const cls = WEAPONS[weapon].cls;
  if (cls === 'blade') return 1.1;
  return cls === 'unarmed' || cls === 'pistol' || cls === 'shotgun' ? 1.35 : 1.25;
}

const SCUFF_CAP = 48;
const SCUFF_LIFE = 45;
/** The scuff shrinks away over its last seconds. */
const SCUFF_FADE = 3;
const SCUFF_SIZE = 0.36;
/** Scuffs per car at once (the oldest goes first). */
const SCUFFS_PER_CAR = 6;
const KNOCKDOWN_DUST_DELAY = 0.35;

/** A scuff in its car's frame: position and the quad's orientation (+Z = the panel's normal). */
interface Scuff {
  vid: number;
  x: number;
  y: number;
  z: number;
  q: THREE.Quaternion;
  /** Sim time it was made; -1 = free. */
  at: number;
}

/**
 * A dark-rimmed dab with the paint rubbed through to pale primer in the middle, so it reads on a black
 * car as well as a cream one, and a couple of scratches across it.
 */
function makeScuffTexture(): THREE.Texture {
  const S = 64;
  const cv = document.createElement('canvas');
  cv.width = cv.height = S;
  const g = cv.getContext('2d')!;
  const gr = g.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
  gr.addColorStop(0, 'rgba(168,160,148,0.5)');
  gr.addColorStop(0.3, 'rgba(120,112,102,0.42)');
  gr.addColorStop(0.58, 'rgba(32,28,25,0.5)');
  gr.addColorStop(0.8, 'rgba(14,12,11,0.62)');
  gr.addColorStop(1, 'rgba(14,12,11,0)');
  g.fillStyle = gr;
  g.fillRect(0, 0, S, S);
  // a couple of scratches across the dab
  g.strokeStyle = 'rgba(205,198,186,0.55)';
  g.lineWidth = 1.5;
  g.beginPath();
  g.moveTo(S * 0.3, S * 0.42);
  g.lineTo(S * 0.72, S * 0.58);
  g.moveTo(S * 0.36, S * 0.62);
  g.lineTo(S * 0.66, S * 0.38);
  g.stroke();
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function createMeleeFxSystem(): System {
  let ctx: GameContext;
  let particles: ParticleService | undefined;
  let vehicles: VehicleService | undefined;
  let low = qualityTier() === 'low';
  let unsubTier: (() => void) | null = null;
  const offs: (() => void)[] = [];
  let lastContactAt = -1;
  let lastFiredAt = -1;
  let contactHeading = 0;
  const later: { at: number; fn: () => void }[] = [];

  // Scuffs: one instanced quad pool, world-space instances that follow their car each frame.
  const scuffs: Scuff[] = Array.from({ length: SCUFF_CAP }, () => ({ vid: -1, x: 0, y: 0, z: 0, q: new THREE.Quaternion(), at: -1 }));
  let scuffMesh: THREE.InstancedMesh | null = null;
  let scuffNext = 0;
  const pose = { x: 0, z: 0, angle: 0 };
  const tmpM = new THREE.Matrix4();
  const tmpQ = new THREE.Quaternion();
  const tmpP = new THREE.Vector3();
  const tmpS = new THREE.Vector3();
  const tmpN = new THREE.Vector3();
  const Y = new THREE.Vector3(0, 1, 0);
  const Z = new THREE.Vector3(0, 0, 1);
  const ray = new THREE.Raycaster();
  const place = { x: 0, y: 0, z: 0, yaw: 0 };

  function ensureScuffMesh(): THREE.InstancedMesh | null {
    if (scuffMesh || typeof document === 'undefined') return scuffMesh;
    const geo = new THREE.PlaneGeometry(SCUFF_SIZE, SCUFF_SIZE);
    // Lit like the bullet marks (fx/impactFx.ts): an unlit decal glows grey on a car at night.
    const mat = new THREE.MeshLambertMaterial({ map: makeScuffTexture(), transparent: true, depthWrite: false });
    scuffMesh = new THREE.InstancedMesh(geo, mat, SCUFF_CAP);
    scuffMesh.name = 'meleeScuffs';
    scuffMesh.count = 0;
    scuffMesh.frustumCulled = false;
    scuffMesh.renderOrder = 6;
    ctx.scene.add(scuffMesh);
    return scuffMesh;
  }

  /** The car's drawn rig (vehicleView adds it to the scene at the car's pose, a 'body' mesh under it). */
  function carRoot(v: Vehicle): THREE.Object3D | null {
    v.body.interpolate(1, pose);
    for (const o of ctx.scene.children) {
      if (Math.abs(o.position.x - pose.x) > 0.25 || Math.abs(o.position.z - pose.z) > 0.25) continue;
      if (o.getObjectByName('body')) return o;
    }
    return null;
  }

  /**
   * Where the bat actually met the bodywork: a ray from the swing, slanting down, against the car's drawn
   * rig. The scuff lies on the panel it hit, facing its normal (the shoulder of a wing or a roof rail, which
   * the high camera sees face on). Null when nothing of the car was in the way (the box placement then).
   */
  function scuffOnMesh(v: Vehicle, pos: V2, dir: V2, s: Scuff): boolean {
    const root = carRoot(v);
    if (!root) return false;
    tmpP.set(pos.x - dir.x * 1.2, 1.35, pos.z - dir.z * 1.2);
    tmpN.set(dir.x, -0.5, dir.z).normalize();
    ray.set(tmpP, tmpN);
    ray.far = 3.5;
    const hit = ray.intersectObject(root, true).find((h) => h.face && (h.object as THREE.Mesh).isMesh);
    if (!hit?.face) return false;
    tmpN.copy(hit.face.normal).transformDirection(hit.object.matrixWorld);
    if (tmpN.y < -0.2) return false; // the underside: not a panel the bat came down on
    tmpP.copy(hit.point).addScaledVector(tmpN, SCUFF_LIFT);
    // Into the car's frame (the rig is rotated about Y by the pose angle and sits at the pose).
    const c = Math.cos(pose.angle);
    const sn = Math.sin(pose.angle);
    const wx = tmpP.x - pose.x;
    const wz = tmpP.z - pose.z;
    s.x = wx * c - wz * sn;
    s.y = tmpP.y;
    s.z = wx * sn + wz * c;
    const nx = tmpN.x * c - tmpN.z * sn;
    const nz = tmpN.x * sn + tmpN.z * c;
    s.q.setFromUnitVectors(Z, tmpN.set(nx, tmpN.y, nz).normalize());
    return true;
  }

  /** A free slot for a scuff on car `vid` (a car keeps a few; beyond that its oldest goes). */
  function nextScuff(vid: number): Scuff {
    let count = 0;
    let oldest: Scuff | null = null;
    for (const s of scuffs) {
      if (s.vid !== vid || s.at < 0) continue;
      count++;
      if (!oldest || s.at < oldest.at) oldest = s;
    }
    if (count >= SCUFFS_PER_CAR && oldest) return oldest;
    const s = scuffs[scuffNext]!;
    scuffNext = (scuffNext + 1) % SCUFF_CAP;
    return s;
  }

  function blowDirection(pos: V2): V2 {
    const p = ctx.services.tryGet('player')?.pos;
    const dx = pos.x - (p?.x ?? pos.x);
    const dz = pos.z - (p?.z ?? pos.z);
    const d = Math.hypot(dx, dz);
    if (d > 0.05) return { x: dx / d, z: dz / d };
    return { x: Math.sin(contactHeading), z: Math.cos(contactHeading) };
  }

  /** The knife at contact: a row of short bright beads down a diagonal arc in front of the player. */
  function slash(pos: V2, heading: number, reach: number): void {
    if (!particles) return;
    const r = reach * 0.55;
    const N = 9;
    for (let i = 0; i < N; i++) {
      const f = i / (N - 1);
      const a = heading + (0.5 - f) * 1.1;
      const y = 1.45 - f * 0.5;
      particles.emit('streak', { x: pos.x + Math.sin(a) * r, y, z: pos.z + Math.cos(a) * r }, { count: 1, intensity: 0.6 + 0.4 * Math.sin(f * Math.PI) });
    }
  }

  function onPersonHit(weapon: WeaponId, pos: V2, knocked: boolean, killed: boolean): void {
    const dir = blowDirection(pos);
    const heavy = isHeavyBlow(weapon);
    const y = contactHeight(weapon);
    const at = { x: pos.x - dir.x * 0.25, y, z: pos.z - dir.z * 0.25 };
    particles?.emit('impactFlash', at, { count: 1, intensity: heavy ? 0.85 : 0.6 });
    particles?.emit('motes', at, { count: heavy ? 6 : 4, dir, intensity: heavy ? 1 : 0.75 });
    if (knocked || killed) {
      // The body lands a beat later: the dust goes up where it comes down.
      const fx = pos.x + dir.x * 0.6;
      const fz = pos.z + dir.z * 0.6;
      later.push({ at: ctx.state.time + KNOCKDOWN_DUST_DELAY, fn: () => particles?.emit('dust', { x: fx, y: 0.15, z: fz }, { count: 7, intensity: 0.9, vel: { x: dir.x, y: 0, z: dir.z } }) });
    }
    const kick = meleeKick(weapon, knocked, killed);
    const rig = ctx.services.tryGet('cameraRig');
    rig?.nudge(dir, kick.nudge);
    rig?.shake(kick.shake);
    ctx.services.tryGet('juice')?.hitStop(meleeHitStop(weapon, knocked, killed));
  }

  function onVehicleHit(weapon: WeaponId, pos: V2, vid: number): void {
    const dir = blowDirection(pos);
    const v = vehicles?.get(vid);
    const h = v ? Math.max(0.4, Math.min(1, v.spec.height * 0.55)) : 0.6;
    const at = { x: pos.x, y: h, z: pos.z };
    particles?.emit('impactFlash', at, { count: 1, intensity: 0.7 });
    particles?.emit('sparks', at, { count: 8, dir: { x: -dir.x, z: -dir.z }, intensity: 0.6 });
    const kick = meleeKick(weapon, false, false);
    const rig = ctx.services.tryGet('cameraRig');
    rig?.nudge(dir, kick.nudge * 0.8);
    rig?.shake(kick.shake);
    ctx.services.tryGet('juice')?.hitStop(HIT_STOP.light);
    if (!v || low || !ensureScuffMesh()) return;
    const s = nextScuff(vid);
    if (!scuffOnMesh(v, pos, dir, s)) {
      const l = v.body.worldToLocal(pos);
      scuffOnBody({ x: l.x, z: l.z }, v.spec.width / 2, v.spec.length / 2, v.spec.height, place);
      s.x = place.x;
      s.y = place.y;
      s.z = place.z;
      s.q.setFromAxisAngle(Y, place.yaw);
    }
    s.vid = vid;
    s.at = ctx.state.time;
  }

  function updateScuffs(alpha: number): void {
    const mesh = scuffMesh;
    if (!mesh) return;
    const now = ctx.state.time;
    let n = 0;
    for (const s of scuffs) {
      if (s.at < 0) continue;
      const age = now - s.at;
      const v = age < SCUFF_LIFE && !low ? vehicles?.get(s.vid) : undefined;
      if (!v || v.destroyed) {
        s.at = -1;
        continue;
      }
      v.body.interpolate(alpha, pose);
      const c = Math.cos(pose.angle);
      const sn = Math.sin(pose.angle);
      tmpP.set(pose.x + s.x * c + s.z * sn, s.y, pose.z - s.x * sn + s.z * c);
      tmpQ.setFromAxisAngle(Y, pose.angle).multiply(s.q);
      const k = age > SCUFF_LIFE - SCUFF_FADE ? (SCUFF_LIFE - age) / SCUFF_FADE : 1;
      tmpS.setScalar(k);
      mesh.setMatrixAt(n++, tmpM.compose(tmpP, tmpQ, tmpS));
    }
    mesh.count = n;
    mesh.visible = n > 0;
    if (n) mesh.instanceMatrix.needsUpdate = true;
  }

  return {
    id: 'melee-fx',
    order: 737,
    init(c) {
      ctx = c;
      unsubTier = onQualityTier((q) => {
        low = q === 'low';
      });
      offs.push(
        c.events.on('meleeContact', (e) => {
          lastContactAt = c.state.time;
          contactHeading = e.heading;
          if (WEAPONS[e.weapon].cls === 'blade') slash(e.pos, e.heading, e.reach);
        }),
        c.events.on('weaponFired', () => {
          lastFiredAt = c.state.time;
        }),
        c.events.on('weaponHit', (e) => {
          if (!wasBlow(WEAPONS[e.weapon].firearm !== null, lastContactAt, lastFiredAt)) return;
          if (e.target === 'vehicle') onVehicleHit(e.weapon, e.pos, e.id);
          else onPersonHit(e.weapon, e.pos, e.knocked, e.killed);
        }),
      );
      c.hooks.registerSnapshot('meleeFx', () => ({ scuffs: scuffMesh?.count ?? 0, pending: later.length }));
    },
    start() {
      particles = ctx.services.tryGet('particles');
      vehicles = ctx.services.tryGet('vehicles');
    },
    update(_dt, alpha) {
      if (later.length) {
        const now = ctx.state.time;
        for (let i = later.length - 1; i >= 0; i--) {
          if (later[i]!.at > now) continue;
          const l = later[i]!;
          later.splice(i, 1);
          l.fn();
        }
      }
      updateScuffs(alpha);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      unsubTier?.();
      later.length = 0;
      if (scuffMesh) {
        ctx.scene.remove(scuffMesh);
        scuffMesh.geometry.dispose();
        const m = scuffMesh.material as THREE.MeshLambertMaterial;
        m.map?.dispose();
        m.dispose();
        scuffMesh = null;
      }
    },
  };
}
